import { useState, useEffect, useCallback } from 'react'
import {
    Box,
    Paper,
    Typography,
    TextField,
    InputAdornment,
    IconButton,
    Button,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Avatar,
    Divider,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Badge,
    Alert,
} from '@mui/material'
import {
    Search,
    Refresh,
    WhatsApp,
    Send,
    Phone,
    Schedule,
    Message,
    Reply,
    CheckCircle,
    Error,
    Image,
    AttachFile,
    PlayArrow,
    Close,
    FilterList,
} from '@mui/icons-material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from '@i18n'
import { useAuthStore } from '@store/authStore'
import { whatsappApi } from '@api/whatsapp'
import { formatDate, formatRelativeTime } from '@utils/helpers'
import Loading from '@components/Common/Loading'
import { toast } from 'react-toastify'

// Status colors
const STATUS_COLORS = {
    new: 'info',
    read: 'default',
    replied: 'success',
    converted: 'primary',
    archived: 'default',
}

// Arabic status labels
const STATUS_LABELS = {
    new: { en: 'New', ar: 'جديد' },
    read: { en: 'Read', ar: 'مقروء' },
    replied: { en: 'Replied', ar: 'تم الرد' },
    converted: { en: 'Converted', ar: 'محول' },
    archived: { en: 'Archived', ar: 'مؤرشف' },
}

// Message type icons
const getMessageTypeIcon = (type) => {
    switch (type) {
        case 'image': return <Image fontSize="small" />
        case 'video': return <PlayArrow fontSize="small" />
        case 'audio': return <PlayArrow fontSize="small" />
        case 'document': return <AttachFile fontSize="small" />
        default: return <Message fontSize="small" />
    }
}

const WhatsAppMessages = () => {
    const { t, language } = useTranslation()
    const { user } = useAuthStore()
    const queryClient = useQueryClient()
    const isRTL = language === 'ar'

    // State
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(20)
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState('')
    const [selectedMessage, setSelectedMessage] = useState(null)
    const [replyDialogOpen, setReplyDialogOpen] = useState(false)
    const [replyText, setReplyText] = useState('')

    // Fetch messages
    const { data: messagesData, isLoading, refetch } = useQuery({
        queryKey: ['whatsapp-messages', page, rowsPerPage, search, statusFilter],
        queryFn: () => whatsappApi.getMessages({
            page: page + 1,
            per_page: rowsPerPage,
            search: search || undefined,
            status: statusFilter || undefined,
        }),
    })

    // Fetch stats
    const { data: statsData } = useQuery({
        queryKey: ['whatsapp-stats'],
        queryFn: () => whatsappApi.getStats(),
    })

    // Reply mutation
    const replyMutation = useMutation({
        mutationFn: ({ messageId, content }) => whatsappApi.sendReply(messageId, content),
        onSuccess: () => {
            toast.success(isRTL ? 'تم إرسال الرد بنجاح' : 'Reply sent successfully')
            setReplyDialogOpen(false)
            setReplyText('')
            setSelectedMessage(null)
            queryClient.invalidateQueries(['whatsapp-messages'])
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to send reply')
        },
    })

    // Status update mutation
    const statusMutation = useMutation({
        mutationFn: ({ messageId, status }) => whatsappApi.updateStatus(messageId, status),
        onSuccess: () => {
            queryClient.invalidateQueries(['whatsapp-messages'])
            queryClient.invalidateQueries(['whatsapp-stats'])
        },
    })

    const messages = messagesData?.data?.messages || []
    const total = messagesData?.data?.total || 0
    const stats = statsData?.data || {}

    const handleSearch = useCallback((e) => {
        setSearch(e.target.value)
        setPage(0)
    }, [])

    const handleOpenReply = (message) => {
        setSelectedMessage(message)
        setReplyDialogOpen(true)
    }

    const handleSendReply = () => {
        if (!replyText.trim() || !selectedMessage) return
        replyMutation.mutate({ messageId: selectedMessage.id, content: replyText })
    }

    const handleStatusChange = (messageId, newStatus) => {
        statusMutation.mutate({ messageId, status: newStatus })
    }

    if (isLoading && messages.length === 0) {
        return <Loading />
    }

    return (
        <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <WhatsApp sx={{ fontSize: 40, color: '#25D366' }} />
                    <Box>
                        <Typography variant="h4" fontWeight={700}>
                            {isRTL ? 'رسائل WhatsApp' : 'WhatsApp Messages'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {isRTL ? 'إدارة الرسائل الواردة والردود' : 'Manage incoming messages and replies'}
                        </Typography>
                    </Box>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={() => refetch()}
                    disabled={isLoading}
                >
                    {isRTL ? 'تحديث' : 'Refresh'}
                </Button>
            </Box>

            {/* Stats Cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2, mb: 3 }}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light', color: 'info.contrastText' }}>
                    <Typography variant="h4" fontWeight={700}>{stats.new || 0}</Typography>
                    <Typography variant="body2">{isRTL ? 'جديد' : 'New'}</Typography>
                </Paper>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.100' }}>
                    <Typography variant="h4" fontWeight={700}>{stats.read || 0}</Typography>
                    <Typography variant="body2">{isRTL ? 'مقروء' : 'Read'}</Typography>
                </Paper>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText' }}>
                    <Typography variant="h4" fontWeight={700}>{stats.replied || 0}</Typography>
                    <Typography variant="body2">{isRTL ? 'تم الرد' : 'Replied'}</Typography>
                </Paper>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                    <Typography variant="h4" fontWeight={700}>{stats.total || 0}</Typography>
                    <Typography variant="body2">{isRTL ? 'الإجمالي' : 'Total'}</Typography>
                </Paper>
            </Box>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small"
                        placeholder={isRTL ? 'بحث برقم الهاتف أو المحتوى...' : 'Search by phone or content...'}
                        value={search}
                        onChange={handleSearch}
                        sx={{ minWidth: 250 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>{isRTL ? 'الحالة' : 'Status'}</InputLabel>
                        <Select
                            value={statusFilter}
                            label={isRTL ? 'الحالة' : 'Status'}
                            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                        >
                            <MenuItem value="">{isRTL ? 'الكل' : 'All'}</MenuItem>
                            <MenuItem value="new">{isRTL ? 'جديد' : 'New'}</MenuItem>
                            <MenuItem value="read">{isRTL ? 'مقروء' : 'Read'}</MenuItem>
                            <MenuItem value="replied">{isRTL ? 'تم الرد' : 'Replied'}</MenuItem>
                            <MenuItem value="archived">{isRTL ? 'مؤرشف' : 'Archived'}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Paper>

            {/* Messages Table */}
            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                                <TableCell>{isRTL ? 'المرسل' : 'Sender'}</TableCell>
                                <TableCell>{isRTL ? 'الرسالة' : 'Message'}</TableCell>
                                <TableCell>{isRTL ? 'الحالة' : 'Status'}</TableCell>
                                <TableCell>{isRTL ? 'التاريخ' : 'Date'}</TableCell>
                                <TableCell align="center">{isRTL ? 'إجراءات' : 'Actions'}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {messages.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                                        <WhatsApp sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
                                        <Typography color="text.secondary">
                                            {isRTL ? 'لا توجد رسائل' : 'No messages found'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                messages.map((msg) => (
                                    <TableRow
                                        key={msg.id}
                                        hover
                                        sx={{
                                            cursor: 'pointer',
                                            bgcolor: msg.status === 'new' ? 'info.lighter' : 'inherit',
                                            '&:hover': { bgcolor: 'action.hover' },
                                        }}
                                    >
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Avatar sx={{ bgcolor: '#25D366', width: 36, height: 36 }}>
                                                    <Phone fontSize="small" />
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {msg.from_name || msg.from_phone}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {msg.from_phone}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 300 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {getMessageTypeIcon(msg.message_type)}
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        fontWeight: msg.status === 'new' ? 600 : 400,
                                                    }}
                                                >
                                                    {msg.message_content || `[${msg.message_type}]`}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={STATUS_LABELS[msg.status]?.[language] || msg.status}
                                                color={STATUS_COLORS[msg.status] || 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={formatDate(msg.received_at)}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {formatRelativeTime(msg.received_at)}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title={isRTL ? 'رد' : 'Reply'}>
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleOpenReply(msg)}
                                                >
                                                    <Reply />
                                                </IconButton>
                                            </Tooltip>
                                            {msg.status === 'new' && (
                                                <Tooltip title={isRTL ? 'تعليم كمقروء' : 'Mark as read'}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleStatusChange(msg.id, 'read')}
                                                    >
                                                        <CheckCircle fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    labelRowsPerPage={isRTL ? 'صفوف لكل صفحة:' : 'Rows per page:'}
                />
            </Paper>

            {/* Reply Dialog */}
            <Dialog
                open={replyDialogOpen}
                onClose={() => { setReplyDialogOpen(false); setSelectedMessage(null); setReplyText(''); }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WhatsApp color="success" />
                        {isRTL ? 'رد على الرسالة' : 'Reply to Message'}
                    </Box>
                    <IconButton onClick={() => setReplyDialogOpen(false)} size="small">
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedMessage && (
                        <Box>
                            {/* Original message */}
                            <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Avatar sx={{ bgcolor: '#25D366', width: 28, height: 28 }}>
                                        <Phone sx={{ fontSize: 16 }} />
                                    </Avatar>
                                    <Typography variant="subtitle2">
                                        {selectedMessage.from_name || selectedMessage.from_phone}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatRelativeTime(selectedMessage.received_at)}
                                    </Typography>
                                </Box>
                                <Typography variant="body2">
                                    {selectedMessage.message_content || `[${selectedMessage.message_type}]`}
                                </Typography>
                            </Paper>

                            {/* Reply input */}
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                placeholder={isRTL ? 'اكتب ردك هنا...' : 'Type your reply here...'}
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReplyDialogOpen(false)}>
                        {isRTL ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={replyMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <Send />}
                        onClick={handleSendReply}
                        disabled={!replyText.trim() || replyMutation.isPending}
                    >
                        {isRTL ? 'إرسال' : 'Send'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default WhatsAppMessages
